import { type CreateFormRequest, FormType } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z, zodNonEmptyString } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useCreateFormMutation } from '../../../graphql/hooks/useCreateFormMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { FormForm } from './FormForm';

type FormValues = CreateFormRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: zodNonEmptyString('Name is required'),
  type: z.enum(FormType),
  url: z.string(),
  externalId: z.string(),
  labelMappings: z.object({
    create: z.array(z.string()),
    update: z.array(z.string()),
  }),
});

export const FormCreateModal = createModal((props) => {
  const { modal } = props;
  const { hide, remove, visible } = modal;
  const { errorToast, successToast, organization } = useAppContext();

  const initialValues: FormValues = {
    name: '',
    type: FormType.Participant,
    url: '',
    externalId: '',
    labelMappings: {
      create: [],
      update: [],
    },
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });
  const type = methods.watch('type');

  const mutation = useCreateFormMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Form created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating form: ${err.message}`);
        });
    },
    [mutation, organization.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a form'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <FormForm type={type} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
